import * as tabAccessRules from '@/rules/tab-access'
import { appIntelModule } from '@/rules/page-access'
import { maAuthenticationGuard, maAuthorizationGuard, maCountryGuard, maDateRangeGuard, maParamsQueryNormalizeGuard } from '@/router/guards'
import { Param, Query, RouteName, TitleFont } from '@/base/constants/route'
import { PRODUCT_FAMILY, PRODUCTS } from '@/base/constants/authorization'

const appIntelRoutes = {
  path: 'app-intelligence',
  name: RouteName.APP_INTELLIGENCE,
  meta: {
    title: 'App Intelligence',
    shortTitle: 'APP',
    icon: 'mobile',
    parent: true,
    productFamily: PRODUCT_FAMILY.ASO,
    requiredProducts: [PRODUCTS.ASO_LITE, PRODUCTS.ASO_BASIC_V2, PRODUCTS.ASO_PRO, PRODUCTS.ASO_BUSINESS, PRODUCTS.COMPASS],
  },
  redirect: { name: RouteName.APP_INTELLIGENCE_COMPASS },
  component: () => import('@/layouts/Dashboard.vue'),
  beforeEnter: maAuthenticationGuard,
  children: [
    {
      path: 'compass',
      name: RouteName.APP_INTELLIGENCE_COMPASS,
      meta: {
        title: 'COMPASS',
        titleFont: TitleFont.HEADLINE,
        sub: true,
      },
      redirect: { name: RouteName.APP_INTELLIGENCE_COMPASS_EXPLORE },
      children: [
        {
          path: 'explore/:trackId?',
          name: RouteName.APP_INTELLIGENCE_COMPASS_EXPLORE,
          meta: {
            title: 'Explore',
            requiredParams: [Param.TRACK_ID],
            requiredQueries: [Query.COUNTRY_CODE],
            analyticsPageName: 'Explore',
            productFamily: PRODUCT_FAMILY.ASO,
            requiredAccess: appIntelModule.compassExploreAccessRule,
          },
          component: () => import('@/pages/competitive-overview/index.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maCountryGuard],
        },
        {
          path: 'trace/:trackId?',
          name: RouteName.APP_INTELLIGENCE_COMPASS_TRACE,
          meta: {
            title: 'Trace',
            requiredParams: [Param.TRACK_ID],
            requiredQueries: [Query.COUNTRY_CODE],
            analyticsPageName: 'Trace',
            productFamily: PRODUCT_FAMILY.ASO,
            requiredAccess: appIntelModule.compassTraceAccessRule,
          },
          component: () => import('@/pages/compass/trace/index.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maCountryGuard],
        },
        {
          path: 'impact/:trackId?',
          name: RouteName.APP_INTELLIGENCE_COMPASS_IMPACT,
          meta: {
            title: 'Impact',
            requiredParams: [Param.TRACK_ID],
            requiredQueries: [Query.COUNTRY_CODE, Query.START_DATE, Query.END_DATE, Query.ACTIVE_APP],
            analyticsPageName: 'Impact',
            productFamily: PRODUCT_FAMILY.ASO,
            disabledAutoScroll: true,
            requiredAccess: appIntelModule.compassImpactAccessRule,
          },
          component: () => import('@/pages/compass/impact/index.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maCountryGuard],
        },
      ],
    },
    {
      path: 'analysis',
      name: RouteName.APP_INTELLIGENCE_ANALYSIS,
      meta: {
        title: 'ANALYSIS',
        titleFont: TitleFont.HEADLINE,
      },
      redirect: { name: RouteName.APP_INTELLIGENCE_ANALYSIS_APP_PROFILE },
      children: [
        {
          path: 'app-profile/:trackId?',
          name: RouteName.APP_INTELLIGENCE_ANALYSIS_APP_PROFILE,
          meta: {
            title: 'App Profile',
            analyticsPageName: 'App Profile',
            disabledAutoScroll: true,
            requiredParams: [Param.TRACK_ID],
            requiredQueries: [Query.COUNTRY_CODE],
            productFamily: PRODUCT_FAMILY.ASO,
            requiredAccess: appIntelModule.appProfileAccessRule,
          },
          component: () => import('@/pages/app-profile/index.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maCountryGuard],
        },
        {
          path: 'publisher-profile/:publisherId?',
          name: RouteName.APP_INTELLIGENCE_ANALYSIS_PUBLISHER_PROFILE,
          meta: {
            title: 'Publisher Profile',
            analyticsPageName: 'Publisher Profile',
            productFamily: PRODUCT_FAMILY.ASO,
            navigationWithoutAppTrack: true,
            requiredParams: [Param.PUBLISHER_ID],
            requiredQueries: [Query.STORE, Query.COUNTRY_FILTER],
            requiredAccess: appIntelModule.publisherProfileAccessRule,
          },
          component: () => import('@/pages/publisher/index.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maCountryGuard],
        },
        {
          path: 'featured-apps',
          name: RouteName.APP_INTELLIGENCE_ANALYSIS_FEATURED_APPS,
          meta: {
            title: 'Featured Apps',
            analyticsPageName: 'Featured Apps',
            productFamily: PRODUCT_FAMILY.ASO,
            navigationWithoutAppTrack: true,
            ignoreChildrenFromMenu: true,
            requiredAccess: appIntelModule.featuredAppsAccessRule,
          },
          component: () => import('@/pages/featured-apps/FeaturedApps.vue'),
          beforeEnter: maAuthorizationGuard,
          redirect: { name: RouteName.APP_INTELLIGENCE_ANALYSIS_FEATURED_APPS_IOS },
          children: [
            {
              path: 'ios',
              name: RouteName.APP_INTELLIGENCE_ANALYSIS_FEATURED_APPS_IOS,
              meta: {
                title: 'Featured Apps',
                analyticsPageName: 'Featured Apps - App Store',
                productFamily: PRODUCT_FAMILY.ASO,
              },
              component: () => import('@/pages/featured-apps/ios/FeaturedAppsIos.vue'),
              redirect: { name: RouteName.APP_INTELLIGENCE_ANALYSIS_FEATURED_APPS_IOS_TODAY_TAB },
              children: [
                {
                  path: 'today',
                  name: RouteName.APP_INTELLIGENCE_ANALYSIS_FEATURED_APPS_IOS_TODAY_TAB,
                  meta: {
                    title: 'Featured Apps',
                    analyticsPageName: 'Featured Apps - App Store - Today',
                    productFamily: PRODUCT_FAMILY.ASO,
                    requiredQueries: [
                      Query.START_DATE,
                      Query.END_DATE,
                      Query.SELECTED_DATE,
                    ],
                  },
                  components: {
                    tab: () => import('@/pages/featured-apps/ios/FeaturedAppsTabToday.vue'),
                  },
                  beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
                },
                {
                  path: 'games',
                  name: RouteName.APP_INTELLIGENCE_ANALYSIS_FEATURED_APPS_IOS_GAMES_TAB,
                  meta: {
                    title: 'Featured Apps',
                    analyticsPageName: 'Featured Apps - App Store - Games',
                    productFamily: PRODUCT_FAMILY.ASO,
                    requiredQueries: [
                      Query.CATEGORY_ID,
                      Query.START_DATE,
                      Query.END_DATE,
                      Query.SELECTED_DATE,
                    ],
                  },
                  components: {
                    tab: () => import('@/pages/featured-apps/ios/FeaturedAppsTabGames.vue'),
                  },
                  beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
                },
                {
                  path: 'apps',
                  name: RouteName.APP_INTELLIGENCE_ANALYSIS_FEATURED_APPS_IOS_APPS_TAB,
                  meta: {
                    title: 'Featured Apps',
                    analyticsPageName: 'Featured Apps - App Store - Apps',
                    productFamily: PRODUCT_FAMILY.ASO,
                    requiredQueries: [
                      Query.CATEGORY_ID,
                      Query.START_DATE,
                      Query.END_DATE,
                      Query.SELECTED_DATE,
                    ],
                  },
                  components: {
                    tab: () => import('@/pages/featured-apps/ios/FeaturedAppsTabApps.vue'),
                  },
                  beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
                },
              ],
            },
            {
              path: 'play',
              name: RouteName.APP_INTELLIGENCE_ANALYSIS_FEATURED_APPS_PLAY,
              meta: {
                title: 'Featured Apps',
                analyticsPageName: 'Featured Apps - Google Play',
                productFamily: PRODUCT_FAMILY.ASO,
              },
              component: () => import('@/pages/featured-apps/play/FeaturedAppsPlay.vue'),
              redirect: { name: RouteName.APP_INTELLIGENCE_ANALYSIS_FEATURED_APPS_PLAY_APPS_TAB },
              children: [
                {
                  path: 'apps',
                  name: RouteName.APP_INTELLIGENCE_ANALYSIS_FEATURED_APPS_PLAY_APPS_TAB,
                  meta: {
                    title: 'Featured Apps',
                    analyticsPageName: 'Featured Apps - Google Play - Apps',
                    productFamily: PRODUCT_FAMILY.ASO,
                    requiredQueries: [
                      Query.CATEGORY_ID,
                      Query.START_DATE,
                      Query.END_DATE,
                      Query.SELECTED_DATE,
                    ],
                  },
                  components: {
                    tab: () => import('@/pages/featured-apps/play/FeaturedAppsTabApps.vue'),
                  },
                  beforeEnter: [maParamsQueryNormalizeGuard, maCountryGuard],
                },
                {
                  path: 'games',
                  name: RouteName.APP_INTELLIGENCE_ANALYSIS_FEATURED_APPS_PLAY_GAMES_TAB,
                  meta: {
                    title: 'Featured Apps',
                    analyticsPageName: 'Featured Apps - Google Play - Games',
                    productFamily: PRODUCT_FAMILY.ASO,
                    requiredQueries: [
                      Query.CATEGORY_ID,
                      Query.START_DATE,
                      Query.END_DATE,
                      Query.SELECTED_DATE,
                    ],
                  },
                  components: {
                    tab: () => import('@/pages/featured-apps/play/FeaturedAppsTabGames.vue'),
                  },
                  beforeEnter: [maParamsQueryNormalizeGuard, maCountryGuard],
                },
                {
                  path: 'family',
                  name: RouteName.APP_INTELLIGENCE_ANALYSIS_FEATURED_APPS_PLAY_FAMILY_TAB,
                  meta: {
                    title: 'Featured Apps',
                    analyticsPageName: 'Featured Apps - Google Play - Family',
                    productFamily: PRODUCT_FAMILY.ASO,
                    requiredQueries: [
                      Query.CATEGORY_ID,
                      Query.AGE_ID,
                      Query.START_DATE,
                      Query.END_DATE,
                      Query.SELECTED_DATE,
                    ],
                  },
                  components: {
                    tab: () => import('@/pages/featured-apps/play/FeaturedAppsTabFamily.vue'),
                  },
                  beforeEnter: [maParamsQueryNormalizeGuard, maCountryGuard],
                },
              ],
            },
          ],
        },
        {
          path: 'category-rankings',
          name: RouteName.APP_INTELLIGENCE_ANALYSIS_CATEGORY_RANKINGS,
          meta: {
            title: 'Category Rankings',
            analyticsPageName: 'Category Rankings',
            productFamily: PRODUCT_FAMILY.ASO,
            ignoreChildrenFromMenu: true,
            requiredAccess: appIntelModule.categoryRankingsAccessRule,
          },
          component: () => import('@/pages/category-rankings/index.vue'),
          beforeEnter: maAuthorizationGuard,
          redirect: { name: RouteName.APP_INTELLIGENCE_ANALYSIS_CATEGORY_RANKINGS_DAILY_RANKING_HISTORY_TAB },
          children: [
            {
              path: 'daily-ranking-history/:trackId?',
              name: RouteName.APP_INTELLIGENCE_ANALYSIS_CATEGORY_RANKINGS_DAILY_RANKING_HISTORY_TAB,
              meta: {
                title: 'Category Rankings',
                analyticsPageName: 'Category Rankings - Daily Ranking History',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [
                  Query.COUNTRY_CODE,
                  Query.DEVICE,
                  Query.START_DATE,
                  Query.END_DATE,
                  Query.RANKING_TYPE,
                  Query.RANKING_FREQUENCY,
                ],
              },
              components: {
                tab: () => import('@/pages/category-rankings/TabDailyRankingHistory.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
            {
              path: 'daily-country-ranking/:trackId?',
              name: RouteName.APP_INTELLIGENCE_ANALYSIS_CATEGORY_RANKINGS_DAILY_COUNTRY_RANKING_TAB,
              meta: {
                title: 'Category Rankings',
                analyticsPageName: 'Category Rankings - Daily Country Ranking',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [
                  Query.ACTIVE_APP,
                  Query.COUNTRY_CODE,
                  Query.DEVICE,
                  Query.DATE,
                  Query.RANKING_TYPE,
                  Query.RANKING_FREQUENCY,
                ],
              },
              components: {
                tab: () => import('@/pages/category-rankings/TabDailyCountryRanking.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
          ],
        },
        {
          path: 'reviews',
          name: RouteName.APP_INTELLIGENCE_ANALYSIS_REVIEWS,
          meta: {
            title: 'Reviews',
            analyticsPageName: 'Reviews',
            productFamily: PRODUCT_FAMILY.ASO,
            ignoreChildrenFromMenu: true,
            disabledAutoScroll: true,
            requiredAccess: appIntelModule.reviewsAccessRule,
          },
          beforeEnter: maAuthorizationGuard,
          redirect: { name: RouteName.APP_INTELLIGENCE_ANALYSIS_REVIEWS_REVIEW_ANALYSIS },
          children: [
            {
              path: 'review-analysis/:trackId?',
              name: RouteName.APP_INTELLIGENCE_ANALYSIS_REVIEWS_REVIEW_ANALYSIS,
              meta: {
                title: 'Reviews',
                analyticsPageName: 'Reviews - Review Analysis',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [
                  Query.ACTIVE_APP,
                  Query.COUNTRY_CODE,
                  Query.LANGUAGE_CODE,
                  Query.START_DATE,
                  Query.END_DATE,
                ],
              },
              components: {
                tab: () => import('@/pages/review/components/MaReviewAnalysis.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
            {
              path: 'most-mentioned-keywords-analysis/:trackId?',
              name: RouteName.APP_INTELLIGENCE_ANALYSIS_REVIEWS_MOST_MENTIONED_KEYWORDS_ANALYSIS,
              meta: {
                title: 'Reviews',
                analyticsPageName: 'Reviews - Most Mentioned Keywords Analysis',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [
                  Query.ACTIVE_APP,
                  Query.COUNTRY_CODE,
                  Query.LANGUAGE_CODE,
                  Query.START_DATE,
                  Query.END_DATE,
                ],
                requiredAccess: tabAccessRules.accessReviewsMostMentionedKeywordsTabRule,
              },
              components: {
                tab: () => import('@/pages/review/components/MaMostMentionedKeywords.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
          ],
          component: () => import('@/pages/review/index.vue'),
        },
        {
          path: 'ratings',
          name: RouteName.APP_INTELLIGENCE_ANALYSIS_RATINGS,
          meta: {
            title: 'Ratings',
            analyticsPageName: 'Ratings',
            productFamily: PRODUCT_FAMILY.ASO,
            ignoreChildrenFromMenu: true,
            disabledAutoScroll: true,
            requiredAccess: appIntelModule.ratingsAccessRule,
          },
          beforeEnter: maAuthorizationGuard,
          redirect: { name: RouteName.APP_INTELLIGENCE_ANALYSIS_RATINGS_TOTAL_RATINGS_TAB },
          children: [
            {
              path: 'total-ratings/:trackId?',
              name: RouteName.APP_INTELLIGENCE_ANALYSIS_RATINGS_TOTAL_RATINGS_TAB,
              meta: {
                title: 'Ratings',
                analyticsPageName: 'Ratings - Total Ratings',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [Query.COUNTRY_CODE, Query.ACTIVE_APP],
              },
              components: {
                tab: () => import('@/pages/ratings/total-ratings/index.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maCountryGuard],
            },
            {
              path: 'daily-ratings/:trackId?',
              name: RouteName.APP_INTELLIGENCE_ANALYSIS_RATINGS_DAILY_RATINGS_TAB,
              meta: {
                title: 'Ratings',
                analyticsPageName: 'Ratings - Daily Ratings',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [Query.COUNTRY_CODE, Query.ACTIVE_APP, Query.START_DATE, Query.END_DATE],
              },
              components: {
                tab: () => import('@/pages/ratings/daily-ratings/index.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
          ],
          component: () => import('@/pages/ratings/index.vue'),
        },
        {
          path: 'similar-apps/:trackId?',
          name: RouteName.APP_INTELLIGENCE_ANALYSIS_SIMILAR_APPS,
          meta: {
            title: 'Similar Apps',
            analyticsPageName: 'Similar Apps',
            productFamily: PRODUCT_FAMILY.ASO,
            ignoreChildrenFromMenu: true,
            disabledAutoScroll: true,
            requiredParams: [Param.TRACK_ID],
            requiredAccess: appIntelModule.similarAppsAccessRule,
          },
          component: () => import('@/pages/similar-apps/index.vue'),
          beforeEnter: maAuthorizationGuard,
          redirect: { name: RouteName.APP_INTELLIGENCE_ANALYSIS_SIMILAR_APPS_TAB },
          children: [
            {
              path: '',
              name: RouteName.APP_INTELLIGENCE_ANALYSIS_SIMILAR_APPS_TAB,
              meta: {
                title: 'Similar Apps',
                analyticsPageName: 'Similar Apps - Similar Apps',
                requiredQueries: [Query.ACTIVE_APP, Query.COUNTRY_CODE, Query.DATE],
              },
              components: {
                tab: () => import('@/pages/similar-apps/components/MaSimilarAppsOfSelectedApp.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
            {
              path: 'listed-as-a-similar-app',
              name: RouteName.APP_INTELLIGENCE_ANALYSIS_SIMILAR_APPS_LISTED_AS_A_SIMILAR_APP_TAB,
              meta: {
                title: 'Similar Apps',
                analyticsPageName: 'Similar Apps - Listed as a Similar App',
                requiredQueries: [Query.ACTIVE_APP, Query.COUNTRY_CODE, Query.DATE],
              },
              components: {
                tab: () => import('@/pages/similar-apps/components/MaAppsSimilarToSelectedApp.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
          ],
        },
        {
          path: 'in-app-events',
          name: RouteName.APP_INTELLIGENCE_ANALYSIS_IN_APP_EVENTS,
          meta: {
            title: 'In App Events',
            analyticsPageName: 'In App Events',
            ignoreChildrenFromMenu: true,
            productFamily: PRODUCT_FAMILY.ASO,
            requiredAccess: appIntelModule.inAppEventsAccessRule,
          },
          component: () => import('@/pages/in-app-events/index.vue'),
          beforeEnter: maAuthorizationGuard,
          redirect: { name: RouteName.APP_INTELLIGENCE_ANALYSIS_IN_APP_EVENTS_CATEGORY_EVENTS_TAB },
          children: [
            {
              path: 'category-events/:trackId?',
              name: RouteName.APP_INTELLIGENCE_ANALYSIS_IN_APP_EVENTS_CATEGORY_EVENTS_TAB,
              meta: {
                title: 'In App Events',
                analyticsPageName: 'In App Events - Category Events',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [Query.COUNTRY_FILTER, Query.START_DATE, Query.END_DATE, Query.CATEGORY_ID],
                ignoredQueries: [Query.COUNTRY_CODE],
              },
              components: {
                tab: () => import('@/pages/in-app-events/MaTabCategoryEvents.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
            {
              path: 'app-events/:trackId?',
              name: RouteName.APP_INTELLIGENCE_ANALYSIS_IN_APP_EVENTS_APP_EVENTS_TAB,
              meta: {
                title: 'In App Events',
                analyticsPageName: 'In App Events - App Events',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [Query.COUNTRY_CODE, Query.START_DATE, Query.END_DATE, Query.CATEGORY_ID],
                ignoredQueries: [Query.COUNTRY_FILTER],
              },
              components: {
                tab: () => import('@/pages/in-app-events/MaTabSelectedAppsEvents.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
          ],
        },
        {
          path: 'promotional-content',
          name: RouteName.APP_INTELLIGENCE_ANALYSIS_PROMOTIONAL_CONTENT,
          meta: {
            title: 'Promotional Content',
            analyticsPageName: 'Promotional Content',
            productFamily: PRODUCT_FAMILY.ASO,
            ignoreChildrenFromMenu: true,
            requiredAccess: appIntelModule.promotionalContentAccessRule,
          },
          component: () => import('@/pages/promotional-content/index.vue'),
          beforeEnter: maAuthorizationGuard,
          redirect: { name: RouteName.APP_INTELLIGENCE_ANALYSIS_PROMOTIONAL_CONTENT_CATEGORY_EVENTS_TAB },
          children: [
            {
              path: 'category-events/:trackId?',
              name: RouteName.APP_INTELLIGENCE_ANALYSIS_PROMOTIONAL_CONTENT_CATEGORY_EVENTS_TAB,
              meta: {
                title: 'Promotional Content',
                analyticsPageName: 'Promotional Content - Category Events',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [Query.COUNTRY_FILTER, Query.START_DATE, Query.END_DATE, Query.CATEGORY_ID],
                ignoredQueries: [Query.COUNTRY_CODE],
              },
              components: {
                tab: () => import('@/pages/promotional-content/MaTabCategoryEvents.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
            {
              path: 'app-events/:trackId?',
              name: RouteName.APP_INTELLIGENCE_ANALYSIS_PROMOTIONAL_CONTENT_APP_EVENTS_TAB,
              meta: {
                title: 'Promotional Content',
                analyticsPageName: 'Promotional Content - App Events',
                requiredParams: [Param.TRACK_ID],
                requiredQueries: [Query.COUNTRY_CODE, Query.START_DATE, Query.END_DATE, Query.CATEGORY_ID],
                ignoredQueries: [Query.COUNTRY_FILTER],
              },
              components: {
                tab: () => import('@/pages/promotional-content/MaTabSelectedAppsEvents.vue'),
              },
              beforeEnter: [maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
            },
          ],
        },
      ],
    },
    {
      path: 'top-charts',
      name: RouteName.APP_INTELLIGENCE_TOP_CHARTS,
      meta: {
        title: 'TOP CHARTS',
        titleFont: TitleFont.HEADLINE,
      },
      redirect: { name: RouteName.APP_INTELLIGENCE_TOP_CHARTS_LEADERBOARD },
      children: [
        {
          path: 'leaderboard',
          name: RouteName.APP_INTELLIGENCE_TOP_CHARTS_LEADERBOARD,
          meta: {
            title: 'Top Charts',
            analyticsPageName: 'Top Charts',
            productFamily: PRODUCT_FAMILY.ASO,
            navigationWithoutAppTrack: true,
            requiredQueries: [Query.COUNTRY_FILTER, Query.END_DATE, Query.STORE, Query.DEVICE, Query.CATEGORY_ID],
            requiredAccess: appIntelModule.topChartsAccessRule,
          },
          component: () => import('@/pages/leaderboard/TopCharts.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
        },
        {
          path: 'biggest-movers',
          name: RouteName.APP_INTELLIGENCE_TOP_CHARTS_BIGGEST_MOVERS,
          meta: {
            title: 'Biggest Movers',
            analyticsPageName: 'Biggest Movers',
            productFamily: PRODUCT_FAMILY.ASO,
            navigationWithoutAppTrack: true,
            requiredQueries: [Query.SORT_TYPE, Query.END_DATE, Query.COUNTRY_FILTER, Query.STORE, Query.DEVICE, Query.CATEGORY_ID],
            requiredAccess: appIntelModule.biggestMoversAccessRule,
          },
          component: () => import('@/pages/leaderboard/BiggestMovers.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
        },
        {
          path: 'biggest-losers',
          name: RouteName.APP_INTELLIGENCE_TOP_CHARTS_BIGGEST_LOSERS,
          meta: {
            title: 'Biggest Losers',
            analyticsPageName: 'Biggest Losers',
            productFamily: PRODUCT_FAMILY.ASO,
            navigationWithoutAppTrack: true,
            requiredQueries: [Query.SORT_TYPE, Query.END_DATE, Query.COUNTRY_FILTER, Query.STORE, Query.DEVICE, Query.CATEGORY_ID],
            requiredAccess: appIntelModule.biggestLosersAccessRule,
          },
          component: () => import('@/pages/leaderboard/BiggestLosers.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
        },
        {
          path: 'new-apps',
          name: RouteName.APP_INTELLIGENCE_TOP_CHARTS_NEW_APPS,
          meta: {
            title: 'New Apps',
            analyticsPageName: 'New Apps',
            productFamily: PRODUCT_FAMILY.ASO,
            navigationWithoutAppTrack: true,
            requiredQueries: [Query.COUNTRY_FILTER, Query.END_DATE, Query.CATEGORY_ID],
            requiredAccess: appIntelModule.newAppsAccessRule,
          },
          component: () => import('@/pages/leaderboard/NewApps.vue'),
          beforeEnter: [maAuthorizationGuard, maParamsQueryNormalizeGuard, maDateRangeGuard, maCountryGuard],
        },
      ],
    },
  ],
}

export default appIntelRoutes
